import { z } from 'zod';
import { StageSchema } from './common.schema';
import { UndoRefSchema } from './kit.schema';
import {
  ARCHIVE_REASON_MAX,
  ARCHIVE_REFUSAL,
  BULK_MAX,
  BULK_REFUSAL,
} from '../../src/domain/order/archive';
import { DELETION_REASON_MAX, DELETION_REFUSAL } from '../../src/domain/order/deletion';

/**
 * The commands on a selection of cases. Section 4 of the request of
 * 24 September 2026: "allow option to select multiple cases in orders, archive,
 * production line, and allow different actions for the selected cases like:
 * delete all, archive all, assign all to a particular department or person".
 *
 * One request per selection, and one answer per case in it, `BulkResult`. Each
 * case is decided by the rules its own endpoint would apply, and a case that
 * cannot be moved does not stop the rest. The request as a whole is refused only
 * for what is wrong with all of it: a permission, a reason, or nothing chosen.
 *
 * The identifiers are plain strings rather than job order numbers: a number that
 * names no case is answered as not in the system, against that case, as the API
 * answers it, rather than refusing the whole selection.
 */
const selection = z
  .array(z.string().trim().min(1))
  .min(1, BULK_REFUSAL.chooseOne)
  .max(BULK_MAX, BULK_REFUSAL.tooMany);

/** `POST /job-orders/bulk/archive`. The reason is optional: most archived cases are finished work. */
export const BulkArchiveSchema = z.object({
  jobOrderNos: selection,
  reason: z.string().trim().max(ARCHIVE_REASON_MAX, ARCHIVE_REFUSAL.reasonTooLong).optional(),
});

/** `POST /job-orders/bulk/unarchive`. Back exactly as they went in. */
export const BulkUnarchiveSchema = z.object({
  jobOrderNos: selection,
});

/**
 * `POST /job-orders/bulk/pay`, `/unpay`, `/release` and `/unrelease`: the
 * money actions on many cases at once, and the Undo of each. The lab's request
 * of 17 October 2026, items 9 and 10.
 */
export const BulkMoneySchema = z.object({
  jobOrderNos: selection,
});

/** `POST /job-orders/bulk/deletion`. One reason for all of them, required, as for one. */
export const BulkDeletionSchema = z.object({
  jobOrderNos: selection,
  reason: z
    .string({ required_error: DELETION_REFUSAL.reasonRequired })
    .trim()
    .min(1, DELETION_REFUSAL.reasonRequired)
    .max(DELETION_REASON_MAX, DELETION_REFUSAL.reasonTooLong),
});

/**
 * `POST /kits/bulk/assign`. To a department, a person, or both.
 *
 * The selection is `kitIds`, from a table of trays such as a stage's, or
 * `jobOrderNos`, from a table of cases such as the orders list, which takes every
 * tray of each case; each is answered by what was sent. A department sends the
 * trays there, never to Ready. A person is given them at the bench they end up
 * at, by the single assign's rule and wording: only somebody who works it, and
 * nobody at all on a case that is on hold. `userId` null takes whoever has them
 * off; left out, it changes nobody but those a move takes the trays away from.
 * Neither a department nor a person is a 400 on `stage`: "Choose a department, a
 * person, or both."
 */
export const BulkAssignSchema = z.object({
  kitIds: selection.optional(),
  jobOrderNos: selection.optional(),
  stage: StageSchema.optional(),
  userId: z.string().nullable().optional(),
});

/** What became of one case in a selection: done, or refused in the words to show against it. */
export const BulkOutcomeSchema = z.object({
  id: z.string(),
  ok: z.boolean(),
  detail: z.string().nullable(),
});

/** The answer to a command on a selection: one outcome per case, in the order sent, and the counts. */
export const BulkResultSchema = z.object({
  results: z.array(BulkOutcomeSchema),
  done: z.number().int().nonnegative(),
  refused: z.number().int().nonnegative(),
  /**
   * `POST /kits/bulk/assign` only: one undo for the whole selection, for the
   * person who sent it and for a moment. Null when nothing changed. Section 6
   * of the request of 5 October 2026.
   */
  undo: UndoRefSchema.nullable().optional(),
});

export type BulkResult = z.infer<typeof BulkResultSchema>;
export type BulkAssignCommand = z.infer<typeof BulkAssignSchema>;
